import {
  signIn as signInNextAuth,
  SignInOptions,
  SignInResponse,
  signOut as signOutNextAuth,
  SignOutParams,
  SignOutResponse,
} from "next-auth/react";
import type { SignInAuthorizationParams } from "next-auth/react";
import { parseUrl, stringifyUrl } from "query-string";
import { disableOneTapAutoSelect } from "src/atoms/GoogleOneTap/google-one-tap";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { appStore } from "src/stores/app/app.store";
import { SignInOrSignUpRequestBody } from "src/types/sign-in-or-sign-up";

// NOTE: Explicitly pass current domain into 'signIn' and 'signOut' param 'callbackUrl'
// to force redirect to current domain instead of NEXTAUTH_URL.
//
// This fixes the issue of NEXTAUTH_URL doesn't contain 'www' but Cloudflare force adds it. This
// causes redirect of 'callbackUrl'
//
// Possible solutions:
// - Configure Cloudflare to not force add 'www'
// or
// - Add 'www' to NEXTAUTH_URL. We tried to do this, but 'www' wasn't there. Need to figure out why
const getOptionsWithCurrentHost = <R extends boolean = true>(
  defaultOptions?: SignInOrSignUpRequestBody | SignOutParams<R>
): SignInOrSignUpRequestBody => {
  const options = { ...(defaultOptions || {}) };

  options.callbackUrl = options.callbackUrl
    ? options.callbackUrl.startsWith("/")
      ? `${window.location.origin}${options.callbackUrl}`
      : options.callbackUrl
    : window.location.href;

  return options;
};

// NODE: Need 'singin=true' query param to track start of a session on Client side and
// run login after this.
//
// Unfortunately there is no out-of-the-box way in 'next-auth' to track this. Check this GitHub
// discussion for updates:
// https://github.com/nextauthjs/next-auth/discussions/1634
const getOptionsWithSignInQueryParam = (
  defaultOptions?: SignInOrSignUpRequestBody
): SignInOrSignUpRequestBody => {
  const options = { ...(defaultOptions || {}) };

  const { url, query } = parseUrl(options.callbackUrl as string);

  const callbackUrl = stringifyUrl({
    url,
    query: { ...query, signin: "true" },
  });

  if (!options.skipSignIn) {
    options.callbackUrl = callbackUrl;
  }

  return options;
};

// NOTE: Need this to track a session change in a hook
const getOptionsWithRedirectFalse = (
  defaultOptions?: SignOutParams<false>
): SignInOrSignUpRequestBody => {
  const options = { ...(defaultOptions || {}) };
  options.redirect = false;

  return options;
};

function signIn(
  provider: AuthProvider,
  options?: SignInOptions,
  args?: SignInAuthorizationParams
): Promise<SignInResponse | undefined> {
  const modifiedOptions = getOptionsWithSignInQueryParam(
    getOptionsWithCurrentHost(options)
  );

  return signInNextAuth(provider, modifiedOptions, args);
}

function signOut<R extends boolean = true>(
  options?: SignOutParams<R>
): Promise<R extends true ? undefined : SignOutResponse> {
  const modifiedOptions = getOptionsWithRedirectFalse(
    getOptionsWithCurrentHost(options)
  ) as SignOutParams<R>;

  disableOneTapAutoSelect();

  appStore.setSigningOut(true);

  const result = signOutNextAuth<R>(modifiedOptions);

  void result.catch(() => appStore.setSigningOut(false));

  return result;
}

export { signIn, signOut, getOptionsWithCurrentHost };
